/**
 * 習慣のスケジュール判定を行うカスタムフック
 *
 * 週間ビュー（Weekly.tsx）と月間ビュー（Monthly.tsx）で
 * 重複していたロジックを共通化したもの。
 *
 * Rails で言えば:
 * - app/models/habit.rb の scheduled_on?(date) メソッドに相当
 * - app/models/habit_log.rb の completed_on?(date) メソッドに相当
 *
 * @example
 * const { isCompleted, isScheduled } = useHabitSchedule()
 *
 * // 今日の予定をチェック
 * if (isScheduled(habit, new Date())) {
 *   console.log('今日は予定あり')
 * }
 *
 * // 完了状態をチェック
 * if (isCompleted(habit, new Date())) {
 *   console.log('完了済み')
 * }
 */

import { useCallback } from 'react'
import { formatDateLocal } from '@/lib/date'
import type { Habit } from '@/types'

/**
 * useHabitSchedule フックの戻り値
 */
interface UseHabitScheduleReturn {
  /**
   * 指定日に習慣が完了しているかを判定
   *
   * @param habit - 判定対象の習慣
   * @param date - 判定対象の日付
   * @returns 完了している場合は true
   */
  isCompleted: (habit: Habit, date: Date) => boolean

  /**
   * 指定日に習慣が予定されているかを判定
   *
   * 頻度タイプに基づいて判定:
   * - daily: 毎日予定あり → 常に true
   * - weekly: 指定された曜日のみ → frequencyDays に含まれるか
   *
   * @param habit - 判定対象の習慣
   * @param date - 判定対象の日付
   * @returns 予定されている場合は true
   */
  isScheduled: (habit: Habit, date: Date) => boolean

  /**
   * 指定日に習慣が予定外完了（追加実行）かを判定
   *
   * 完了しているが、予定日ではない場合に true を返す。
   *
   * @param habit - 判定対象の習慣
   * @param date - 判定対象の日付
   * @returns 予定外完了の場合は true
   */
  isExtraCompletion: (habit: Habit, date: Date) => boolean
}

export function useHabitSchedule(): UseHabitScheduleReturn {
  /**
   * 指定日に習慣が完了しているかを判定
   *
   * habit.completedDates 配列に日付文字列が含まれているかで判定。
   * completedDates は Rails 側で HabitLog レコードから生成される。
   */
  const isCompleted = useCallback((habit: Habit, date: Date): boolean => {
    const dateStr = formatDateLocal(date)
    return habit.completedDates?.includes(dateStr) ?? false
  }, [])

  /**
   * 指定日に習慣が予定されているかを判定
   *
   * 頻度タイプによって判定ロジックが異なる:
   * - daily: 毎日実行する習慣なので常に true
   * - weekly: frequencyDays（曜日の配列）に含まれる日のみ true
   *
   * frequencyDays の値は JavaScript の Date.getDay() と同じ:
   * 0=日曜, 1=月曜, ..., 6=土曜
   */
  const isScheduled = useCallback((habit: Habit, date: Date): boolean => {
    if (habit.frequencyType === 'daily') {
      return true
    }
    // weekly の場合: 曜日が frequencyDays に含まれているか
    return habit.frequencyDays.includes(date.getDay())
  }, [])

  /**
   * 指定日に習慣が予定外完了（追加実行）かを判定
   *
   * 完了しているが、予定日ではない場合に true を返す。
   * 予定外の日でも達成を記録できる機能のために使用。
   */
  const isExtraCompletion = useCallback(
    (habit: Habit, date: Date): boolean => {
      return isCompleted(habit, date) && !isScheduled(habit, date)
    },
    [isCompleted, isScheduled]
  )

  return { isCompleted, isScheduled, isExtraCompletion }
}
